import type { Article, CreativeWork, Organization, Product, Service } from 'schema-dts';

import { config } from './config';
import type {
    ArticleSchemaInput,
    CreativeWorkSchemaInput,
    ProductSchemaInput,
    SchemaInput,
    SchemaType,
    ServiceSchemaInput,
} from './types/common';
import type { ItemListSchemaConfig, NonStringThing, SchemaOutput } from './types/itemList';
import { stripHtmlTags, Result, ok, fail, isOk, safeString } from './utils';
import { logger } from './utils/logger';
import { validateSchemaInput } from './utils/validation';

/**
 * Validate schema output for debugging
 */
const validateSchemaOutput = (schema: SchemaOutput): void => {
    if (!schema['@type']) {
        logger.warn('Generated schema object missing required @type property', { schema });
    }

    const schemaName =
        schema['@type'] === 'Product' ||
        schema['@type'] === 'Service' ||
        schema['@type'] === 'Article' ||
        schema['@type'] === 'CreativeWork'
            ? schema.name
            : undefined;

    if (!schemaName) {
        logger.warn(`${schema['@type']} schema missing required name property`, { schema });
    }
};

/**
 * Get organization reference with config support
 */
const getOrganizationReference = (organizationName?: string): Organization | undefined => {
    if (organizationName === 'Storyblocks') {
        return config.getOrganizationSchema();
    }
    return organizationName
        ? {
              '@type': 'Organization' as const,
              name: organizationName,
          }
        : undefined;
};

/**
 * Get publisher reference with config support
 */
const getPublisherReference = (organizationName?: string) => {
    if (organizationName === 'Storyblocks') {
        return config.getOrganizationSchema();
    }
    return organizationName
        ? {
              '@type': 'Organization' as const,
              name: organizationName,
          }
        : undefined;
};

/**
 * ItemSchemaBuilder - Consistent builder pattern for all schema types
 */
class ItemSchemaBuilder<T extends SchemaOutput> {
    protected schema: T;

    constructor(type: T['@type'], title: string) {
        this.schema = {
            '@type': type,
            name: safeString(title),
        } as T;
    }

    addDescription(description?: string): this {
        if (description) {
            (this.schema as unknown as Record<string, unknown>).description = stripHtmlTags(description);
        }
        return this;
    }

    addImage(image?: string): this {
        if (image) {
            (this.schema as unknown as Record<string, unknown>).image = image;
        }
        return this;
    }

    addUrl(url?: string): this {
        if (url) {
            (this.schema as unknown as Record<string, unknown>).url = url;
        }
        return this;
    }

    addOffer(price?: string, currency?: string): this {
        if (price && currency) {
            (this.schema as unknown as Record<string, unknown>).offers = {
                '@type': 'Offer',
                price: safeString(price),
                priceCurrency: safeString(currency).toUpperCase(),
            };
        }
        return this;
    }

    validate(): this {
        validateSchemaOutput(this.schema);
        return this;
    }

    build(): T {
        return this.schema;
    }
}

/**
 * Generate product item with validation
 */
const generateProductItem = (item: ProductSchemaInput): Result<Product> => {
    const validation = validateSchemaInput(item);
    if (!isOk(validation)) {
        logger.warn('Invalid product input', { errors: validation.error });
        return fail(new Error('Invalid product input'));
    }

    const builder = new ItemSchemaBuilder<Product>('Product', item.title);
    const product = builder
        .addDescription(item.description)
        .addImage(item.image)
        .addUrl(item.url)
        .addOffer(item.price, item.currency)
        .validate()
        .build();

    return ok(product);
};

/**
 * Generate service item with validation
 */
const generateServiceItem = (item: ServiceSchemaInput, organizationName?: string): Result<Service> => {
    const validation = validateSchemaInput(item);
    if (!isOk(validation)) {
        logger.warn('Invalid service input', { errors: validation.error });
        return fail(new Error('Invalid service input'));
    }

    const builder = new ItemSchemaBuilder<Service>('Service', item.title);
    const service = builder
        .addDescription(item.description)
        .addImage(item.image)
        .addUrl(item.url)
        .addOffer(item.price, item.currency)
        .validate()
        .build();

    const organization = getOrganizationReference(organizationName);
    if (organization) {
        service.provider = organization;
    }

    return ok(service);
};

/**
 * Generate article item with validation
 */
const generateArticleItem = (item: ArticleSchemaInput, organizationName?: string): Result<Article> => {
    const validation = validateSchemaInput(item);
    if (!isOk(validation)) {
        logger.warn('Invalid article input', { errors: validation.error });
        return fail(new Error('Invalid article input'));
    }

    const article: Article = {
        '@type': 'Article',
        name: safeString(item.title),
        headline: safeString(item.headline || item.title),
        ...(item.description && { description: stripHtmlTags(item.description) }),
        ...(item.image && { image: item.image }),
        ...(item.url && { url: item.url }),
    };

    const publisher = getPublisherReference(organizationName);
    if (publisher) {
        article.publisher = publisher;
    }

    validateSchemaOutput(article);
    return ok(article);
};

/**
 * Generate creative work item with validation
 */
const generateCreativeWorkItem = (item: CreativeWorkSchemaInput, organizationName?: string): Result<CreativeWork> => {
    const validation = validateSchemaInput(item);
    if (!isOk(validation)) {
        logger.warn('Invalid creative work input', { errors: validation.error });
        return fail(new Error('Invalid creative work input'));
    }

    const builder = new ItemSchemaBuilder<CreativeWork>('CreativeWork', item.title);
    const creativeWork = builder
        .addDescription(item.description)
        .addImage(item.image)
        .addUrl(item.url)
        .validate()
        .build();

    const organization = getOrganizationReference(organizationName);
    if (organization) {
        creativeWork.creator = organization;
    }

    return ok(creativeWork);
};

/**
 * Generate schema item with error handling
 */
const generateSchemaItem = (
    schemaType: SchemaType,
    item: SchemaInput,
    organizationName?: string,
): Result<SchemaOutput> => {
    switch (schemaType) {
        case 'product':
            return generateProductItem(item as ProductSchemaInput);
        case 'service':
            return generateServiceItem(item as ServiceSchemaInput, organizationName);
        case 'article':
            return generateArticleItem(item as ArticleSchemaInput, organizationName);
        case 'creative-work':
            return generateCreativeWorkItem(item as CreativeWorkSchemaInput, organizationName);
        default: {
            const error = new Error(`Unsupported schema type: ${schemaType}`);
            logger.error('Unsupported schema type', { schemaType });
            return fail(error);
        }
    }
};

/**
 * Generate ItemList structured data for various content types
 * Enhanced with validation and error handling
 *
 * @param config Configuration object containing schema type, items, and metadata
 * @returns Structured data object ready for JSON-LD serialization, or null if no items
 */
export const generateItemListSchema = <T extends SchemaInput>(
    config: ItemListSchemaConfig<T>,
): NonStringThing | null => {
    const { schemaType, heading, description, items, url, organizationName } = config;

    if (!items?.length) {
        logger.debug('No items provided for ItemList schema generation');
        return null;
    }

    // Generate schema items with error handling
    const itemListElements: SchemaOutput[] = [];
    const errors: Array<{ index: number; error: unknown }> = [];

    items.forEach((item, index) => {
        const result = generateSchemaItem(schemaType, item, organizationName);
        if (isOk(result)) {
            itemListElements.push(result.data);
        } else {
            errors.push({ index, error: result.error });
            logger.warn(`Failed to generate schema for item at index ${index}`, {
                schemaType,
                error: result.error,
            });
        }
    });

    // Return null if no valid items were generated
    if (itemListElements.length === 0) {
        logger.error('Failed to generate any valid schema items', {
            totalItems: items.length,
            errors,
        });
        return null;
    }

    const itemList: NonStringThing = {
        '@type': 'ItemList',
        name: safeString(heading),
        ...(description && { description: stripHtmlTags(description) }),
        numberOfItems: itemListElements.length,
        ...(url && { url }),
        itemListElement: itemListElements,
    };

    logger.info('Generated ItemList schema', {
        schemaType,
        itemCount: itemListElements.length,
        skippedCount: errors.length,
        hasUrl: !!url,
        organizationName,
    });

    return itemList;
};
